import { IAbstractRecord } from '@models';
import FormattingService from '@services/FormattingService';

export const transformPolicyArrayToObject = (
  dataArray: IAbstractRecord[],
  paymentDivisions: Record<string, string>
) => {
  if (!dataArray.length) {
    return {};
  }

  const list: IAbstractRecord = {};

  dataArray.forEach((item, index: number) => {
    const billType = item.parentBill_ParentBillCategory;
    const policyName = item.policy_PolicyNumber;
    const certificateNumber = item.policy_CertificateNumber;
    const policyType = item.policy_Type;

    list[index] = {
      policyId: item.policy_Id,
      parentBillId: item.parentBill_Id,
      PolicyNumber:
        billType === 'PRODUCTION'
          ? policyType === 'CERTIFICATE'
            ? `${policyName} ${certificateNumber}`
            : policyName
          : policyType === 'CERTIFICATE'
            ? `${policyName} ${certificateNumber} ${item.linkedAmendment_AmendmentNumber} ${item.linkedAmendment_AmendmentLineNumber}`
            : `${policyName} ${item.linkedAmendment_AmendmentNumber} ${item.linkedAmendment_AmendmentLineNumber}`,
      PaymentDivisionType:
        paymentDivisions[item.policy_PaymentDivisionType] || 'Equivalent',
      PaymentMode: item.policy_PaymentMode?.Title || 'Annually',
      TotalPremium: item.payableBills_TotalPremium,
      AmountReceived: item.policy_TotalAmountReceived,
      TotalCommission: item.payableBills_TotalCommission,
      TaxOnCommission: item.payableBills_TaxOnCommission,
      AmountPaid: item.payableBills_AmountPaid,
      OutstandingPayable:
        Number(item.payableBills_TotalPremium) -
        Number(item.payableBills_AmountReceived) -
        (Number(item.payableBills_TotalCommission) -
          Number(item.payableBills_TaxOnCommission) -
          Number(item.payableBills_AmountPaid)),
      Currency: item.receivableBills_Currency?.Symbol,
    };
  });

  return list;
};

export const transformReceivableBillsArrayToObject = (
  dataArray: IAbstractRecord[]
) => {
  if (!dataArray?.length) {
    return {};
  }

  const receivableBills = dataArray?.filter(
    (bill) => bill.accounting_Bills_BillType === 'RECEIVABLE'
  );

  return receivableBills.reduce((acc, item) => {
    const policyNumber =
      item.accounting_Bills_PolicyID?.views?.Production_all?.properties
        ?.PolicyNumber;
    const certificateNumber =
      item.accounting_Bills_PolicyID?.views?.Production_all?.properties
        ?.CertificateNumber;
    const policyType = item.policy_Type;

    acc[item.accounting_Bills_Id] = {
      Id: item.accounting_Bills_Id,
      BillType:
        item.parentBill_ParentBillCategory === 'PRODUCTION'
          ? FormattingService.formatEnumToDisplay(
              item.parentBill_ParentBillCategory
            )
          : item.linkedAmendment_AmendmentNumber,
      reference:
        item.parentBill_ParentBillCategory === 'PRODUCTION'
          ? policyType === 'CERTIFICATE'
            ? `${policyNumber} ${certificateNumber}`
            : policyNumber
          : policyType === 'CERTIFICATE'
            ? `${policyNumber} ${certificateNumber} ${item.linkedAmendment_AmendmentNumber} ${item.linkedAmendment_AmendmentLineNumber}`
            : `${policyNumber} ${item.linkedAmendment_AmendmentNumber} ${item.linkedAmendment_AmendmentLineNumber}`,
      BillNumber: item.accounting_Bills_BillNumber,
      DueDate: item.accounting_Bills_DueDate,
      AmountDue: item.accounting_Bills_AmountDue,
      NetPremium: item.accounting_Bills_NetPremium,
      TotalPremium: item.accounting_Bills_TotalPremium,
      AmountReceived: item.accounting_Bills_AmountReceived,
      OutstandingBalance: item.accounting_Bills_OutstandingBalance,
      AmendmentNumber: item.linkedAmendment_AmendmentNumber,
      BillStatus: item.accounting_Bills_IsVoided
        ? 'Voided'
        : FormattingService.formatEnumToDisplay(
            item.accounting_Bills_BillStatus
          ),
      Currency: item.accounting_Bills_Currency?.Symbol,
    };
    return acc;
  }, {});
};

export const transformPayableBillsArrayToObject = (
  dataArray: IAbstractRecord[]
) => {
  if (!dataArray?.length) {
    return {};
  }

  return dataArray?.reduce((acc, item) => {
    const policyNumber =
      item.accounting_Bills_PolicyID?.views?.Production_all?.properties
        ?.PolicyNumber;
    const certificateNumber =
      item.accounting_Bills_PolicyID?.views?.Production_all?.properties
        ?.CertificateNumber;
    const policyType = item.policy_Type;

    acc[item.accounting_Bills_Id] = {
      Id: item.accounting_Bills_Id,
      BillType:
        item.parentBill_ParentBillCategory === 'PRODUCTION'
          ? FormattingService.formatEnumToDisplay(
              item.parentBill_ParentBillCategory
            )
          : item.linkedAmendment_AmendmentNumber,
      reference:
        item.parentBill_ParentBillCategory === 'PRODUCTION'
          ? policyType === 'CERTIFICATE'
            ? `${policyNumber} ${certificateNumber}`
            : policyNumber
          : policyType === 'CERTIFICATE'
            ? `${policyNumber} ${certificateNumber} ${item.linkedAmendment_AmendmentNumber} ${item.linkedAmendment_AmendmentLineNumber}`
            : `${policyNumber} ${item.linkedAmendment_AmendmentNumber} ${item.linkedAmendment_AmendmentLineNumber}`,
      BillNumber: item.accounting_Bills_BillNumber,
      DueDate: item.accounting_Bills_DueDate,
      Discount: item.accounting_Bills_Discount,
      TotalCommission: item.accounting_Bills_TotalCommission,
      TaxOnCommission: item.accounting_Bills_TaxOnCommission,
      AmountReceived: item.accounting_Bills_AmountPaid,
      OutstandingBalance: item.accounting_Bills_OutstandingBalance,
      BillStatus: item.accounting_Bills_IsVoided
        ? 'Voided'
        : FormattingService.formatEnumToDisplay(
            item.accounting_Bills_BillStatus
          ),
      Currency: item.accounting_Bills_Currency?.Symbol,
    };
    return acc;
  }, {});
};
